import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUserAuth } from "../../context/userAuthContext";
import {
    Home,
    Users,
    Store,
    Package,
    BarChart3,
    Settings,
    AlertCircle,
    Warehouse,
    ClipboardList,
    TrendingUp,
    Activity,
    Database,
} from "lucide-react";

// Import sub-components
import StoreManager from "./StoreManager";
import ProductManagerEnhanced from "./ProductManagerEnhanced";
import UserManager from "./UserManager";
import StockManager from "./StockManager";
import DashboardOverview from "./DashboardOverview";
import InventarioSucursales from "./InventarioSucursales";
import EstadisticasAnuales from "./EstadisticasAnuales";
import AnalisisAvanzado from "./AnalisisAvanzado";
import ProductImporter from "./ProductImporter";
import LivePF from "./LivePF";
import LiveStats from "./LiveStats";

const NewAdminDashboard = () => {
    const { user, userDataLoading, hasPermission } = useUserAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const tabFromUrl = searchParams.get("tab") || "dashboard";
    const [activeTab, setActiveTab] = useState(tabFromUrl);

    // Sincronizar tab con URL
    useEffect(() => {
        setActiveTab(tabFromUrl);
    }, [tabFromUrl]);

    useEffect(() => {
        console.log("🐔 AdminDashboard - User:", {
            uid: user?.uid,
            email: user?.email,
            role: user?.role,
            permissions: user?.permissions,
            userDataLoading,
        });
    }, [user, userDataLoading]);

    // Verificar si el usuario tiene permisos para acceder
    useEffect(() => {
        if (!userDataLoading && user && !hasPermission('canAccessAdmin')) {
            navigate('/notauthorized');
        }
    }, [user, userDataLoading, hasPermission, navigate]);

    if (userDataLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="relative">
                        <div className="w-20 h-20 border-4 border-gray-200 rounded-full"></div>
                        <div className="absolute top-0 left-0 w-20 h-20 border-4 border-[#FF0000] border-t-transparent rounded-full animate-spin"></div>
                    </div>
                    <p className="text-gray-600 mt-6 font-medium">Cargando Panel...</p>
                </div>
            </div>
        );
    }

    if (!user || !hasPermission('canAccessAdmin')) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
                <div className="text-center max-w-md w-full bg-white rounded-2xl p-8 shadow-lg border border-red-100">
                    <AlertCircle className="h-20 w-20 text-red-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Acceso Denegado</h2>
                    <p className="text-gray-600 mb-2">
                        Rol: <strong className="text-red-500">{user?.role || "No autorizado"}</strong>
                    </p>
                    <p className="text-sm text-gray-500 mb-6">
                        No tienes permisos para acceder al panel de administración
                    </p>
                    <button
                        onClick={() => navigate("/")}
                        className="mt-6 px-6 py-3 bg-[#FF0000] text-white rounded-xl hover:bg-[#CC0000] transition-all duration-300 font-medium shadow-lg w-full"
                    >
                        Volver al Inicio
                    </button>
                </div>
            </div>
        );
    }

    const tabs = [
        {
            id: "dashboard",
            label: "Dashboard",
            icon: Home,
            component: DashboardOverview,
            permission: 'canAccessAdmin'
        },
        {
            id: "stores",
            label: "Sucursales",
            icon: Store,
            component: StoreManager,
            permission: 'canManageStores'
        },
        {
            id: "products",
            label: "Productos",
            icon: Package,
            component: ProductManagerEnhanced,
            permission: 'canManageProducts'
        },
        {
            id: "product-importer",
            label: "Importar Productos",
            icon: Package,
            component: ProductImporter,
            permission: 'canManageProducts'
        },
        {
            id: "stock",
            label: "Inventario",
            icon: Warehouse,
            component: StockManager,
            permission: 'canManageProducts'
        },
        {
            id: "inventario-sucursales",
            label: "Inventario Sucursales",
            icon: ClipboardList,
            component: InventarioSucursales,
            permission: 'canManageProducts'
        },
        {
            id: "estadisticas-anuales",
            label: "Estadísticas Anuales",
            icon: TrendingUp,
            component: EstadisticasAnuales,
            permission: 'canViewReports'
        },
        {
            id: "analisis-avanzado",
            label: "Análisis Avanzado",
            icon: Activity,
            component: AnalisisAvanzado,
            permission: 'canViewReports'
        },
        {
            id: "livepf",
            label: "LivePF",
            icon: Database,
            component: LivePF,
            permission: 'canAccessAdmin'
        },
        {
            id: "livestats",
            label: "LiveStats",
            icon: TrendingUp,
            component: LiveStats,
            permission: 'canAccessAdmin'
        },
        {
            id: "users",
            label: "Usuarios y Permisos",
            icon: Users,
            component: UserManager,
            permission: 'canManageUsers'
        },
        {
            id: "reports",
            label: "Reportes",
            icon: BarChart3,
            component: () => <ComingSoon title="Reportes" />,
            permission: 'canViewReports'
        },
    ];

    // Filtrar tabs según permisos
    const availableTabs = tabs.filter(tab => hasPermission(tab.permission));

    const renderTabContent = () => {
        const activeTabConfig = availableTabs.find((tab) => tab.id === activeTab);
        if (!activeTabConfig) {
            return (
                <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 text-center">
                    <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Sección no encontrada</h3>
                    <p className="text-gray-500">Esta sección no existe o no tienes permisos para acceder</p>
                </div>
            );
        }
        const Component = activeTabConfig.component;
        return <Component />;
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Page Content */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {renderTabContent()}
            </main>
        </div>
    );
};

// Coming Soon Component
const ComingSoon = ({ title }) => (
    <div className="bg-white rounded-2xl p-8 md:p-12 shadow-sm border border-gray-100 text-center">
        <div className="bg-gray-100 p-6 rounded-2xl inline-block mb-4">
            <Settings className="h-12 w-12 text-gray-400" strokeWidth={2} />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-500">Esta sección estará disponible próximamente</p>
    </div>
);

export default NewAdminDashboard;

